import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { DragDropContext, Droppable, DropResult, DraggableLocation } from "@hello-pangea/dnd";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { OrderRequestData } from "./Domain/RemoteOrderRequests.types";
import { ServiceType } from "../../../features/Booking-Form/typings/booking-service-administration.types";
import { useRemoteFetchOrderRequestDetail } from "../../../hooks/Remote/Services/OrderRequests/useRemoteFetchOrderRequestDetail";
import {
  UpdateServicesDto,
  useRemoteUpdateBookingServicesByAdmin
} from "../../../hooks/Remote/Services/OrderRequests/useRemoteUpdateBookingServicesByAdmin";
import { BookingServicesDto } from "../../../features/Booking-Form/typings/booking.types";
import { BookingType } from "../../../features/Booking-Form/typings/booking-inputs";
import {
  filterDataByStatus,
  findTargetStatus,
  getOrderRequestColumnIndex,
  isLockedOrderRequestColumn,
  listBgColor,
  listTitle,
  ORDER_REQUEST_COLUMN_ARCHIVE,
  ORDER_REQUEST_COLUMN_CANCELLED,
  ORDER_REQUEST_DROPPABLE_IDS,
  ORDER_REQUEST_LIST_COLUMN_COUNT
} from "./functions/orderRequestListContents.functions";
import { DraggableCardItem } from "./DraggableCardItem.component";

type Item = OrderRequestData;

type ItemList = Item[];

// Reorder items in a single list
export const reorder = (list: Item[], startIndex: number, endIndex: number): Item[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

// Move item from one list to another
export const move = (
  source: Item[],
  destination: Item[],
  droppableSource: DraggableLocation,
  droppableDestination: DraggableLocation
): Record<string, Item[]> => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(droppableSource.index, 1);
  destClone.splice(droppableDestination.index, 0, removed);

  return {
    [droppableSource.droppableId]: sourceClone,
    [droppableDestination.droppableId]: destClone
  };
};

type OrderRequestListContentProps = {
  data: OrderRequestData[];
  filterByType: ServiceType[];
  setFilteredData: Dispatch<SetStateAction<OrderRequestData[]>>;
  setSelectedOrder: (o: OrderRequestData) => void;
  refetchOrderRequests: () => void;
};

export function OrderRequestListContents({
  data,
  filterByType,
  setFilteredData,
  setSelectedOrder,
  refetchOrderRequests
}: Readonly<OrderRequestListContentProps>) {
  const theme = useTheme();

  const { mutateAsync: fetchDetail } = useRemoteFetchOrderRequestDetail();
  const { mutateAsync: postOrderRequest } = useRemoteUpdateBookingServicesByAdmin();

  const [listItems, setListItems] = useState<ItemList[]>(
    Array.from({ length: ORDER_REQUEST_LIST_COLUMN_COUNT }, () => [])
  );
  const [isFolded, setIsFolded] = useState<{ [key: number]: boolean }>({
    [0]: false, // received / ORDERED
    [1]: false, // in progress / IN_PROGRESS
    [2]: false, // ready / READY
    [3]: false, // archive (past date)
    [4]: false // cancelled booking
  });

  /** update the status of the dragged item by destination's list index */
  async function handleDragEnd(targetListIndex: number, itemId: number) {
    const targetStatus = findTargetStatus(targetListIndex);
    const updated = data;

    const i = updated.findIndex(item => item.id === itemId);
    updated[i].deliveryStatus = targetStatus;
    const targetItem = updated[i];

    fetchDetail(targetItem.bookingId).then(detailData => {
      const catData: BookingServicesDto[] = detailData.caterings.map(cat => ({
        serviceType: ServiceType.Catering,
        status: cat.status,
        serviceId: cat.id,
        setUpTime: cat.setupTime,
        qty: cat.quantity,
        orderedQty: cat.orderedQty,
        availableQty: cat.availableQty
      }));
      const equipData: BookingServicesDto[] = detailData.equipments.map(equip => ({
        serviceType: ServiceType.Bookable_Equipment,
        status: equip.status,
        serviceId: equip.id,
        setUpTime: equip.setupTime,
        qty: equip.quantity
      }));
      const layoutData: BookingServicesDto[] = detailData.roomLayouts.map(layout => ({
        serviceType: ServiceType.Room_Layout,
        status: layout.status,
        serviceId: layout.id,
        setUpTime: layout.setupTime
      }));

      const target: UpdateServicesDto[] = [
        {
          bookingId: targetItem.bookingId,
          bookingType:
            targetItem.placeOrZoneType === "zone"
              ? BookingType.CONFERENCEZONE
              : BookingType.WORKPLACE, // later, we should be able to choose when service can be assigned for the place
          deliveryStatus: targetItem.deliveryStatus,
          servicesInfo: [...catData, ...equipData, ...layoutData]
        }
      ];

      postOrderRequest(target).then(() => {
        setFilteredData(updated);
        refetchOrderRequests();
      });
    });
  }

  const onDragEnd = (result: DropResult) => {
    const { source, destination, draggableId: draggedItemId } = result;

    if (!destination) return;

    const sInd = getOrderRequestColumnIndex(source.droppableId);
    const dInd = getOrderRequestColumnIndex(destination.droppableId);
    if (sInd === -1 || dInd === -1) {
      return;
    }
    // selected/dropped item id
    const droppedItemId = Number.parseInt(draggedItemId, 10);

    if (isLockedOrderRequestColumn(sInd) || isLockedOrderRequestColumn(dInd)) {
      return;
    }

    if (sInd === dInd) {
      // dragged within the same list
      const items = reorder(listItems[sInd], source.index, destination.index);
      const newState = [...listItems];
      newState[sInd] = items;
      setListItems(newState);
    } else {
      // dragged end on the another list
      const result = move(listItems[sInd], listItems[dInd], source, destination);
      const newState = [...listItems];
      newState[sInd] = result[source.droppableId];
      newState[dInd] = result[destination.droppableId];

      // drag end => update list item - draggableId(item id) and droppableId(list id)
      // by droppableId => find the item by draggableId and update the status of the item => fetch
      handleDragEnd(dInd, droppedItemId).then(() => setListItems(newState));
    }
  };

  const stylesByListFold = (listFolded: boolean) => {
    return {
      writingMode: listFolded ? "vertical-lr" : "initial",
      listBoxWidth: listFolded ? "auto" : 405,
      listBoxMinWidth: listFolded ? 60 : 400,
      listBoxMinHeight: listFolded ? "60vh" : "inherit",
      listBoxTitlePr: listFolded ? 1 : 0,
      listFoldingBtnTransform: listFolded ? "rotate(270deg)" : "rotate(0deg)",
      listItemDisplay: listFolded ? "none" : "inherit"
    };
  };

  const handleListFolding = (itemIndex: number) =>
    setIsFolded(prev => ({ ...prev, [itemIndex]: !prev[itemIndex] }));

  useEffect(() => {
    // group the data by status (columns: Received → In Progress → Ready → Archive → Cancelled)
    const { archiveItems, cancelledItems, receivedItems, progressItems, preparedItems } =
      filterDataByStatus(data);
    setListItems([receivedItems, progressItems, preparedItems, archiveItems, cancelledItems]);
  }, [data]);

  return (
    <Box data-testid="b-dnd-container" sx={{ display: "flex" }}>
      <DragDropContext onDragEnd={onDragEnd}>
        {ORDER_REQUEST_DROPPABLE_IDS.map((droppableId, itemIndex) => {
          const item = listItems[itemIndex];
          const listFolded = isFolded[itemIndex];

          return (
            <Droppable
              droppableId={droppableId}
              key={droppableId}
              isDropDisabled={isLockedOrderRequestColumn(itemIndex)}
            >
              {(provided, snapshot) => (
                <Box
                  data-testid={`droppable-listBox-${itemIndex}`}
                  ref={provided.innerRef}
                  sx={[
                    {
                      background: listBgColor(snapshot.isDraggingOver, theme),
                      py: 1,
                      width: stylesByListFold(listFolded).listBoxWidth,
                      minWidth: stylesByListFold(listFolded).listBoxMinWidth,
                      minHeight: stylesByListFold(listFolded).listBoxMinHeight,
                      mr: 1
                    },
                    itemIndex === 0 ? { ml: 0 } : { ml: 1 }
                  ]}
                  {...provided.droppableProps}
                >
                  {/* title of each item list */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      pl: 1,
                      pr: stylesByListFold(listFolded).listBoxTitlePr,
                      writingMode: stylesByListFold(listFolded).writingMode
                    }}
                    data-testid={`droppable-listBox-${itemIndex}-title`}
                  >
                    {/* list folding button */}
                    <IconButton
                      data-testid={`expandButton-${itemIndex}`}
                      sx={{
                        transform: stylesByListFold(listFolded).listFoldingBtnTransform,
                        transition: theme.transitions.create("transform", {
                          duration: theme.transitions.duration.shortest
                        })
                      }}
                      onClick={() => handleListFolding(itemIndex)}
                    >
                      <ExpandMore />
                    </IconButton>

                    <Typography
                      sx={{
                        ml: 0.5,
                        fontWeight: 600,
                        writingMode: stylesByListFold(listFolded).writingMode
                      }}
                    >
                      {listTitle(itemIndex, listItems)}
                    </Typography>
                  </Box>

                  {/* list of item */}
                  <Box
                    sx={{
                      height: "66vh",
                      overflow: "overlay",
                      display: stylesByListFold(listFolded).listItemDisplay
                    }}
                    data-testid={`droppable-listBox-${itemIndex}-content-parent`}
                  >
                    {item.map((item, index) => (
                      <DraggableCardItem
                        key={item.id}
                        item={item}
                        index={index}
                        itemIndex={itemIndex}
                        filterByType={filterByType}
                        setSelectedOrder={setSelectedOrder}
                        isArchive={itemIndex === ORDER_REQUEST_COLUMN_ARCHIVE}
                        isBookingCancelled={itemIndex === ORDER_REQUEST_COLUMN_CANCELLED}
                      />
                    ))}
                  </Box>
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          );
        })}
      </DragDropContext>
    </Box>
  );
}
